import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';

// mui icons
import Assignment from '@mui/icons-material/Assignment';

// core components
import Card from '../md/components/Card/Card.jsx';
import CardBody from '../md/components/Card/CardBody.jsx';
import CardIcon from '../md/components/Card/CardIcon.jsx';
import CardHeader from '../md/components/Card/CardHeader.jsx';

import extendedTablesStyle from '../assets/jss/crm/views/extendedTablesStyle.jsx';

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch } from '../utils/utils';

const firmDownloaderStyles = (theme) => ({
  root: {
    border: '1px solid black',
    width: '800px',
    backgroundColor: 'white',
    padding: '6px',
  },
  titleP: {
    fontWeight: 600,
    color: 'green',
  },
});

const FirmDropdown = ({ value, firmIdNamePairs, changeFunc }) => {
  return (
    <select value={value} onChange={(event) => changeFunc(event)}>
      <option key={0} value={0}>
        Choose Target Firm ...
      </option>
      {firmIdNamePairs.map((pair) => (
        <option key={pair.firmId} value={pair.firmId}>
          {pair.firmName}
        </option>
      ))}
    </select>
  );
};

class FirmDownloaderUnstyled extends React.Component {
  state = {
    running: false,
    chosenFirmId: 0,
  };

  useDropdown = true;

  handleCustomerFirmIdChange(event) {
    const newId = event.target.value;
    this.setState({ chosenFirmId: newId });
  }

  doDownload() {
    this.setState({ running: true });
    const firmId = this.state.chosenFirmId;
    const fileName = this.computeFileNameForFirmWithId(firmId);
    const uri = `/api/firm/${firmId}/asXml`;
    fetch(uri).then((response) => {
      response.blob().then((blob) => {
        this.setState({ running: false });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
      });
    });
  }

  computeFileNameForFirmWithId(firmId) {
    return 'SomeFirm.xml';
  }

  render() {
    const { classes, firmIdNamePairs } = this.props;
    const showDownloadButton = !this.state.running && this.state.chosenFirmId;
    return (
      <div className={classes.root}>
        <div className={classes.titleP}>Download XML Representation of Firm</div>
        <div>
          <FirmDropdown
            value={this.state.chosenFirmId}
            firmIdNamePairs={firmIdNamePairs}
            changeFunc={(event) => this.handleCustomerFirmIdChange(event)}
          />
          <span style={{ marginLeft: '20px' }} />
          {!!showDownloadButton && <input type="button" value="Download .xml File" onClick={() => this.doDownload()} />}
        </div>
      </div>
    );
  }
}

const FirmDownloader = withStyles(firmDownloaderStyles)(FirmDownloaderUnstyled);

const clientDownloaderStyles = (theme) => ({
  root: {
    border: '1px solid black',
    width: '800px',
    backgroundColor: 'white',
    padding: '6px',
  },
  titleP: {
    fontWeight: 600,
    color: 'green',
  },
});

class ClientDownloaderUnstyled extends React.Component {
  state = {
    running: false,
    chosenFirmId: 0,
    chosenStatus: 'Prospect',
  };

  useDropdown = true;

  handleCustomerFirmIdChange(event) {
    const newId = event.target.value;
    this.setState({ chosenFirmId: parseInt(newId) });
  }

  handleStatusChange(event) {
    const newStatus = event.target.value;
    this.setState({ chosenStatus: newStatus });
  }

  doDownload() {
    this.setState({ running: true });
    const firmId = this.state.chosenFirmId;
    const status = this.state.chosenStatus;
    const fileName = this.computeFileNameForFirmWithId(firmId);
    const uri = `/api/firm/${firmId}/clientsWithStatus/${status}/asCsv`;
    fetch(uri).then((response) => {
      response.blob().then((blob) => {
        this.setState({ running: false });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
      });
    });
  }

  computeFileNameForFirmWithId(firmId) {
    const { firmIdNamePairs } = this.props;
    const pairForThisFirm = firmIdNamePairs.find((pair) => pair.firmId === firmId);
    const name = pairForThisFirm.firmName;
    const firstSpaceLoc = name.indexOf(' ');
    const firstWordOfName = firstSpaceLoc > 0 ? name.substring(0, firstSpaceLoc) : name;
    const status = this.state.chosenStatus;
    const statusName = status === 'Prospect' ? 'Prospects' : status === 'Client' ? 'Clients' : 'Alumni';
    return `${firstWordOfName}_${statusName}.csv`;
  }

  render() {
    const { classes, firmIdNamePairs } = this.props;
    const showDownloadButton = !this.state.running && this.state.chosenFirmId;
    const fileName = !!showDownloadButton ? this.computeFileNameForFirmWithId(this.state.chosenFirmId) : '.csv file';
    const buttonLabel = `Download ${fileName}`;
    return (
      <div className={classes.root}>
        <div className={classes.titleP}>Download Some Clients of Firm</div>
        <div>
          Firm:&nbsp;&nbsp;&nbsp;
          <FirmDropdown
            value={this.state.chosenFirmId}
            firmIdNamePairs={firmIdNamePairs}
            changeFunc={(event) => this.handleCustomerFirmIdChange(event)}
          />
          <span style={{ marginLeft: '20px' }} />
          Status:
          <select onChange={(event) => this.handleStatusChange(event)}>
            <option>Prospect</option>
            <option>Client</option>
            <option>Alum</option>
          </select>
          <span style={{ marginLeft: '20px' }} />
          {!!showDownloadButton && <input type="button" value={buttonLabel} onClick={() => this.doDownload()} />}
        </div>
      </div>
    );
  }
}

const ClientDownloader = withStyles(clientDownloaderStyles)(ClientDownloaderUnstyled);

class FirmDownloadPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      unauthorized: false,
      providerIdsAndNames: [],
    };
  }

  componentDidMount() {
    const role = this.props.loggedInRole;
    const onLocalMachine = window.location.hostname === 'localhost';
    if (onLocalMachine || role === 'SuperAdmin' || role === 'AllevaRep') {
      this.fetchFirmData();
    } else {
      this.setState({ unauthorized: true });
    }
  }

  fetchFirmData() {
    this.setState({ querying: true });
    const uri = '/api/firm/providerIdsAndNames';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      this.setState({ querying: false, providerIdsAndNames: json });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  firmIdNamePairs() {
    return this.providerIdsAndNames.map((pair) => {
      return { id: pair.firmId, name: pair.firmName };
    });
  }

  downloadDbFootprintsReport() {
    const dateStr = DateTime.local().toISODate();

    const uri = `/api/superAdmin/dbFootprintsReport`;
    fetch(uri).then((response) => {
      response.blob().then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DB Footprints ${dateStr}.csv`;
        a.click();
      });
    });
  }

  render() {
    if (this.state.unauthorized) {
      return (
        <div>
          <h2>Sorry! You're not allowed to be here.</h2>;
          <h3>You need to be logged in as an AllevaRep or SuperAdmin.</h3>;
        </div>
      );
    }

    const { classes } = this.props;

    return (
      <Card>
        <CardHeader color="allevaOrange" icon>
          <CardIcon color="allevaOrange">
            <Assignment />
          </CardIcon>
          <h4 className={classes.cardIconTitle}>Download Firm</h4>
        </CardHeader>
        <CardBody>
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <FirmDownloader firmIdNamePairs={this.state.providerIdsAndNames} />
          </div>
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <ClientDownloader firmIdNamePairs={this.state.providerIdsAndNames} />
          </div>
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <button onClick={() => this.downloadDbFootprintsReport()}>Download DB Footprints Report</button>
          </div>
        </CardBody>
      </Card>
    );
  }
}

const mapStateToProps = (state) => ({ loggedInRole: state.userInfo.role });

const actionProducers = {
  setApiResponseInfo,
};

export default connect(mapStateToProps, actionProducers)(withStyles(extendedTablesStyle)(FirmDownloadPage));
